/* eslint-disable @typescript-eslint/no-magic-numbers */
import { PcbLayoutFootprintPadShape, PcbNodeTypes, PcbViaThermalReliefConductorAngle, PcbViaThermalReliefConductors, PcbViaThermalReliefTypes, } from "@buildwithflux/models";
import { Vector2 } from "three";
import { ZeroVector2 } from "../common";
import { createShapeFromBakedRules } from "../shapes";
import { positionShapeRelativeToRootLayout } from "./positionShapeRelativeToRootLayout";
/**
 * Examines type and rule configuration of specified baked node to determine if thermal reliefs should
 * be applied. If thermal reliefs are required, strong typing for the pcb node is inferred
 */
export function isThermalReliefRequired(pcbNode) {
    if (pcbNode.type !== PcbNodeTypes.pad) {
        return false;
    }
    const pad = pcbNode;
    if (!pad.bakedRules.rotationRelativeToRootLayout) {
        return false;
    }
    if (!pad.bakedRules.positionRelativeToRootLayout) {
        return false;
    }
    if (!pad.bakedRules.thermalReliefConnectConfig) {
        return false;
    }
    if (pad.bakedRules.thermalReliefType !== PcbViaThermalReliefTypes.reliefConnect) {
        return false;
    }
    return true;
}
/**
 * Creates polygon for single thermal relief conductor based in input parameters; conductor width, keep out width,
 * left and right tip length
 */
function createConductorPolygon(conductorWidth, keepOutWidth, tipLengthLeft, tipLengthRight) {
    return [
        new Vector2(conductorWidth * -0.5, keepOutWidth * +0.5),
        new Vector2(conductorWidth * +0.5, keepOutWidth * +0.5),
        new Vector2(conductorWidth * +0.5, keepOutWidth * -0.5 + -tipLengthLeft),
        // Rough curve approximation
        new Vector2(conductorWidth * +0.3, keepOutWidth * -0.5 + -tipLengthLeft * 0.3),
        new Vector2(conductorWidth * +0.0, keepOutWidth * -0.5),
        // Rough curve approximation
        new Vector2(conductorWidth * -0.3, keepOutWidth * -0.5 + -tipLengthRight * 0.3),
        new Vector2(conductorWidth * -0.5, keepOutWidth * -0.5 + -tipLengthRight),
    ];
}
/**
 * Create thermal relief geometry for custom shaped pad. Note the approach taken in this case is quite different to that of
 * regular rectangular and circular pads. This is because the spec and desired behavior for edge cases that are unique to/arise
 * with, custom pads, is still being developed. The initial solution in this case is to define a thermal reliefs conductor as a
 * single line of copper expanding from one side of a custom shaped pad to another. Currently this will interfere with holes in
 * some cases - when stencil holes are introduced, that issue will be resolved
 */
function createThermalReliefForCustomPad(rules, keepOutWidth) {
    const { thermalReliefConnectConfig } = rules;
    const isConductorsDiagonal = thermalReliefConnectConfig?.angle === PcbViaThermalReliefConductorAngle.fourtyFiveDegree;
    const isConductorsFour = thermalReliefConnectConfig?.conductors === PcbViaThermalReliefConductors.four;
    // TODO: add logger
    const shape = createShapeFromBakedRules(rules, 0);
    if (!shape) {
        return undefined;
    }
    const shapePoints = shape.getPoints();
    if (shapePoints.length < 3) {
        return undefined;
    }
    const shapeBounds = shapePoints.reduce((bounds, point) => ({
        l: Math.min(point.x, bounds.l ?? point.x),
        r: Math.max(point.x, bounds.r ?? point.x),
        t: Math.max(point.y, bounds.t ?? point.y),
        b: Math.min(point.y, bounds.b ?? point.y),
    }), {
        l: undefined,
        r: undefined,
        t: undefined,
        b: undefined,
    });
    const shapeW = Math.abs((shapeBounds.l ?? 0) - (shapeBounds.r ?? 0));
    const shapeH = Math.abs((shapeBounds.t ?? 0) - (shapeBounds.b ?? 0));
    const shapeMajor = Math.max(shapeH, shapeW);
    const shapeCenterX = ((shapeBounds.l ?? 0) + (shapeBounds.r ?? 0)) * 0.5;
    const shapeCenterY = ((shapeBounds.t ?? 0) + (shapeBounds.b ?? 0)) * 0.5;
    // For the diagonal case, choose a conductor length by largest dimension of shape. This ensures better
    // coverage of the conductor geometry from conductor's start to end. Three is used to improve connectivity;
    // one (with added half) times keep out width, per end, to improve geometric overlap of conductor with
    // surrounding fill
    const conductorLengthH = (isConductorsDiagonal ? shapeMajor : shapeW) + keepOutWidth * 3;
    const conductorLengthV = (isConductorsDiagonal ? shapeMajor : shapeH) + keepOutWidth * 3;
    const conductorWidth = Math.min(shapeW, shapeH, thermalReliefConnectConfig?.conductorWidth ?? 0);
    const conductorAngle = isConductorsDiagonal ? 0.25 * Math.PI : 0;
    const polygons = [
        // Vertical line of copper extending from top to bottom of shape
        createConductorPolygon(conductorWidth, conductorLengthV, 0, 0).map((it) => it.rotateAround(ZeroVector2, conductorAngle).add(new Vector2(shapeCenterX, shapeCenterY))),
    ];
    if (isConductorsFour) {
        polygons.push(
        // Horizontal line of copper extending from left to right of shape
        createConductorPolygon(conductorLengthH, conductorWidth, 0, 0).map((it) => it.rotateAround(ZeroVector2, conductorAngle).add(new Vector2(shapeCenterX, shapeCenterY))));
    }
    return polygons;
}
export function isThermalReliefRelated(rule) {
    return (rule.key === "thermalRelief" ||
        rule.key === "thermalReliefConductorAngle" ||
        rule.key === "thermalReliefConductorWidth" ||
        rule.key === "thermalReliefConductors");
}
/**
 * Creates thermal relief geometry that defines the shape of thermal conductors of a pad.
 *
 * the conductor geometry is used as a "positive on a negative". in the absence of thermal reliefs, pads provide a shape
 * profile which is cutout from the fill (ie, negative). this function is intended to add thermal relief features to the
 * geometry of the pad cutout, thereby producing the specified thermal reliefs
 */
export function createThermalReliefConductorPolygons(bakedRules, keepOutWidth) {
    // Expand range of keep out by significant ratio to ensure connectivity with pad for cases where; conductor is very
    // wide or, 90deg angle value applied in which case ratio will ensure overlap with surrounding fill to avoid conductor
    // being omitted by close (but not overlapping) geometry
    keepOutWidth *= 1.25;
    // Take a different approach for custom pads is asset present
    if (bakedRules.asset) {
        const polygons = createThermalReliefForCustomPad(bakedRules, keepOutWidth);
        // If thermal relief polygons obtained for this custom shaped pad, return them, otherwise attempt to generate thermal
        // reliefs for pad based on non-custom-shape parameters
        if (polygons) {
            return polygons.map((polygon) => {
                if (bakedRules.positionRelativeToRootLayout && bakedRules.rotationRelativeToRootLayout) {
                    return positionShapeRelativeToRootLayout(polygon, bakedRules.positionRelativeToRootLayout, bakedRules.rotationRelativeToRootLayout);
                }
                else {
                    return polygon;
                }
            });
        }
    }
    const { size, padShape, cornerRadius, thermalReliefConnectConfig } = bakedRules;
    const isCircularPad = padShape === PcbLayoutFootprintPadShape.circular;
    const isConductorsDiagonal = thermalReliefConnectConfig.angle === PcbViaThermalReliefConductorAngle.fourtyFiveDegree;
    const isConductorsFour = thermalReliefConnectConfig.conductors === PcbViaThermalReliefConductors.four;
    // Ensure conductor width is limited by width and/or height of node
    const conductorWidth = Math.min(size.x, size.y, thermalReliefConnectConfig.conductorWidth);
    const circleRadius = Math.min(size.x, size.y) * 0.5;
    const radiusTR = isCircularPad ? circleRadius : cornerRadius?.topRight ?? 0;
    const radiusBR = isCircularPad ? circleRadius : cornerRadius?.bottomRight ?? 0;
    const radiusBL = isCircularPad ? circleRadius : cornerRadius?.bottomLeft ?? 0;
    const radiusTL = isCircularPad ? circleRadius : cornerRadius?.topLeft ?? 0;
    if (isConductorsDiagonal) {
        // Generate reusable point-base shape for angular conductors (which don't require 'conductor tips')
        const conductorPoints = createConductorPolygon(conductorWidth, keepOutWidth *
            1.25 /* Add an extension for diagonal case to close diagonal gap on contact between (rectangular) pad and thermal conductor. An extra
        25% comfortably accounts for the gap/notch that would otherwise result between pad and 45deg conductor */, 0, 0);
        // Distance ratio inwards from rectangle corner, along 45 deg vector. Use to calculate
        // correction amount for placement of conductor
        const ratio = Math.sin(Math.PI * 0.25);
        const inverseInset = 1 - ratio;
        // Calculate corrections to inset conductors to account for corner radii if present
        const insetTR = inverseInset * radiusTR;
        const insetBR = inverseInset * radiusBR;
        const insetBL = inverseInset * radiusBL;
        const insetTL = inverseInset * radiusTL;
        // Scale length of conductors that are on 45deg to ensure overlapping connection
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        conductorPoints.forEach((point) => {
            point.y *= 1 + ratio;
        });
        const polygons = [
            // Top right
            conductorPoints.map((it) => it
                .clone()
                .add(new Vector2(0, keepOutWidth * 0.5))
                .rotateAround(ZeroVector2, Math.PI * -0.25)
                .add(new Vector2(size.x * +0.5 - insetTR, size.y * +0.5 - insetTR))),
            // Bottom left
            conductorPoints.map((it) => it
                .clone()
                .add(new Vector2(0, keepOutWidth * 0.5))
                .rotateAround(ZeroVector2, Math.PI * +0.75)
                .add(new Vector2(size.x * -0.5 + insetBL, size.y * -0.5 + insetBL))),
        ];
        if (isConductorsFour) {
            polygons.push(
            // Bottom right
            conductorPoints.map((it) => it
                .clone()
                .add(new Vector2(0, keepOutWidth * 0.5))
                .rotateAround(ZeroVector2, Math.PI * -0.75)
                .add(new Vector2(size.x * +0.5 - insetBR, size.y * -0.5 + insetBR))), 
            // Top left
            conductorPoints.map((it) => it
                .clone()
                .add(new Vector2(0, keepOutWidth * 0.5))
                .rotateAround(ZeroVector2, Math.PI * 0.25)
                .add(new Vector2(size.x * -0.5 + insetTL, size.y * +0.5 - insetTL))));
        }
        return polygons.map((polygon) => {
            if (bakedRules.positionRelativeToRootLayout && bakedRules.rotationRelativeToRootLayout) {
                return positionShapeRelativeToRootLayout(polygon, bakedRules.positionRelativeToRootLayout, bakedRules.rotationRelativeToRootLayout);
            }
            else {
                return polygon;
            }
        });
    }
    else {
        // Scale length of conductors that are on 45deg to ensure overlapping connection
        const ratio = 1.05;
        const conB_bl = Math.max(0, 0.5 * conductorWidth - (size.x * 0.5 - radiusBL));
        const conB_br = Math.max(0, 0.5 * conductorWidth - (size.x * 0.5 - radiusBR));
        const conT_tr = Math.max(0, 0.5 * conductorWidth - (size.x * 0.5 - radiusTR));
        const conT_tl = Math.max(0, 0.5 * conductorWidth - (size.x * 0.5 - radiusTL));
        const polygons = [
            // Top
            createConductorPolygon(conductorWidth, keepOutWidth * ratio, conT_tr, conT_tl).map((it) => it
                .clone()
                .rotateAround(ZeroVector2, Math.PI * +0.0)
                .add(new Vector2(0, (size.y + keepOutWidth) * +0.5))),
            // Bottom
            createConductorPolygon(conductorWidth, keepOutWidth * ratio, conB_bl, conB_br).map((it) => it
                .clone()
                .rotateAround(ZeroVector2, Math.PI * +1.0)
                .add(new Vector2(0, (size.y + keepOutWidth) * -0.5))),
        ];
        if (isConductorsFour) {
            const conR_tr = Math.max(0, 0.5 * conductorWidth - (size.y * 0.5 - radiusTR));
            const conR_br = Math.max(0, 0.5 * conductorWidth - (size.y * 0.5 - radiusBR));
            const conL_tl = Math.max(0, 0.5 * conductorWidth - (size.y * 0.5 - radiusTL));
            const conL_bl = Math.max(0, 0.5 * conductorWidth - (size.y * 0.5 - radiusBL));
            polygons.push(
            // Right
            createConductorPolygon(conductorWidth, keepOutWidth * ratio, conR_br, conR_tr).map((it) => it
                .clone()
                .rotateAround(ZeroVector2, Math.PI * -0.5)
                .add(new Vector2((size.x + keepOutWidth) * +0.5, 0))), 
            // Left
            createConductorPolygon(conductorWidth, keepOutWidth * ratio, conL_tl, conL_bl).map((it) => it
                .clone()
                .rotateAround(ZeroVector2, Math.PI * +0.5)
                .add(new Vector2((size.x + keepOutWidth) * -0.5, 0))));
        }
        return polygons.map((polygon) => {
            if (bakedRules.positionRelativeToRootLayout && bakedRules.rotationRelativeToRootLayout) {
                return positionShapeRelativeToRootLayout(polygon, bakedRules.positionRelativeToRootLayout, bakedRules.rotationRelativeToRootLayout);
            }
            else {
                return polygon;
            }
        });
    }
}
//# sourceMappingURL=ThermalReliefs.js.map